/**
 * checkbox-list.js
 */

import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash.get';
import FormSectionTitle from 'components/menu-drawer/form-section-title';



// the default structure of the checkbox list value is an array of option objects
// which must be in the form of { id, name }.

// however the value can be customized using getOptionChecked and setOptionChecked
// such as the case for dog objects where the value is an array of integers.
export default function CheckboxList(props) {
  const {
    formContext: formContextProp,
    name,
    rules,
    label,
    helperText,
    options,
    defaultValue,
    customOther,
    otherMaxLength = 250,
    focusHandlers,
    renderOption,
    getOptionChecked: getCheckedProp,
    setOptionChecked: setCheckedProp,
    disableErrorMessage,
    dataTestId,
  } = props;
  const scrollRef = React.useRef();

  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const error = get(formContext.formState.errors, name);
  const hasError = Boolean(error);


  React.useEffect(() => {
    if (hasError && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasError]);


  const getOptionChecked = getCheckedProp ||
    ((option, checkList) => Boolean(checkList.find(i => i.id === option.id)));


  const setOptionChecked = setCheckedProp ||
    ((option, checkList, isChecked) => isChecked
      ? [...checkList, option]
      : checkList.filter(i => i.id !== option.id));


  const getCustomOther = (option, checkList) => {
    const listItem = checkList.find(i => i.id === option.id);
    return listItem && listItem.customOtherName ? listItem.customOtherName : '';
  }


  const setCustomOther = (option, checkList, customValue) => {
    return checkList.find(i => i.id === option.id)
      ? checkList.map(i => i.id === option.id ? {...i, customOtherName: customValue} : i)
      : [...checkList, {...option, customOtherName: customValue}];
  }


  if (!options) return null;

  return (
    <>
      {/* scroll position on error */}
      <div ref={scrollRef} style={{ position: 'relative', top: -80 }}></div>

      <Controller
        control={formContext.control}
        name={name}
        rules={rules}
        defaultValue={defaultValue || []}
        render={({ field: { ref, ...inputProps } }) => (
          <FormControl
            component="fieldset"
            error={Boolean(error)}
            ref={ref}
            sx={{ flex: 1, width: 'auto' }}
            data-testtype='checkbox-list'
            onFocus={e => {
              if (focusHandlers) {
                focusHandlers.onFocus(e);
              }
            }}
            onBlur={e => {
              if (focusHandlers) {
                focusHandlers.onBlur(e);
              }
            }}
          >

            {/* label */}
            <FormSectionTitle sx={{ mb: 1 }}>{label}</FormSectionTitle>

            {/* error message */}
            {!disableErrorMessage && Boolean(error) &&
            <FormHelperText sx={{ mt: 0, mb: .5 }}>
              {error?.message}
            </FormHelperText>}

            {/* helper text */}
            {Boolean(helperText) &&
            <Typography variant='subtext-small' sx={{ mb: 3 }}>
              {helperText}
            </Typography>}

            {/* checkboxes */}
            <FormGroup 
              ref={ref} 
              sx={{ display: 'flex', pt: 1, pb: 2 }}
              data-testid={dataTestId || ''}
            >
              <Grid
                container
                spacing={2}
              >
                {options.map(option =>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={option.id}
                    className='flex-hc'
                    style={{ paddingTop: 0, paddingBottom: 0 }}
                  >
                    <Box sx={{ display: 'flex', maxWidth: '100%', flex: 1 }}>
                      <FormControlLabel
                        label={renderOption ? renderOption(option) : option.name}
                        sx={{mr: 1}}
                        control={
                          <Checkbox
                            size="medium"
                            color="primary"
                            checked={getOptionChecked(option, inputProps.value || [])}
                            onChange={e => inputProps.onChange(setOptionChecked(option, inputProps.value || [], e.target.checked))}
                            data-testid={option.id}
                          />
                        }
                      />
                      {customOther && option.name === 'Other' &&
                      <TextField
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: otherMaxLength }}
                        value={getCustomOther(option, inputProps.value || [])}
                        onChange={e => inputProps.onChange(setCustomOther(option, inputProps.value || [], e.target.value))}
                        data-testid='custom-other-input'
                      />}
                    </Box>
                  </Grid>)}
              </Grid>
            </FormGroup>

          </FormControl>
        )}
      />
    </>
  );
}